import type { ObjectId } from "@bbj-types/utils";
import { IconButtonVariant } from "@inputs/button/IconButtonVariant";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { type IPaperButtonCollapseProps, PaperButtonCollapse } from "@surfaces/PaperButtonCollapse";
import { type ReactNode, useLayoutEffect, useState } from "react";
import type { IResolverCreateCandidateChallenge } from "../logics/types";
import { FormCreateCandidateChallenge } from "./form";
import { useFormCreateCandidateChallenge } from "./logics";

export interface IPaperCreateCandidateChallengeProps extends Omit<IPaperButtonCollapseProps, "label" | "children"> {
  excludeChallenges?: ObjectId[];
  onSubmit?: (data: IResolverCreateCandidateChallenge) => void | Promise<void>;
  label?: ReactNode;
  onOpenModal?: () => void;
}

export const PaperCreateCandidateChallenge = ({
  open: controlledOpen,
  onToggleOpen,
  excludeChallenges,
  onSubmit,
  label,
  onOpenModal,
}: IPaperCreateCandidateChallengeProps) => {
  const [loading, setLoading] = useState(false);
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlledOpen ?? localOpen;
  const { control, setValue, handleSubmit, reset } = useFormCreateCandidateChallenge();

  const handleToggleOpen = (open: boolean) => {
    if (loading) {
      return;
    }
    if (onOpenModal) {
      onOpenModal();
      return;
    }
    if (controlledOpen === undefined) {
      setLocalOpen(open);
    }
    if (onToggleOpen) {
      onToggleOpen(open);
    }
  };

  const submit = handleSubmit(async (data) => {
    if (onSubmit) {
      setLoading(true);
      try {
        await onSubmit(data);
        handleToggleOpen(!open);
      } finally {
        setLoading(false);
      }
    }
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Reset form on Paper open
  useLayoutEffect(() => {
    if (open) {
      reset();
    }
  }, [open]);

  const defaultLabel = (
    <>
      <AddIcon color="primary" />
      <Typography variant="h4" color="primary">
        Nouvelle thématique
      </Typography>
    </>
  );

  return (
    <PaperButtonCollapse
      open={open}
      onToggleOpen={handleToggleOpen}
      label={label ?? defaultLabel}
      slotProps={{
        labelStack: {
          direction: "row",
          alignItems: "center",
          py: 1,
          px: 3,
          gap: 1,
        },
        collapseStack: {
          pt: 1,
          pb: 2,
          px: 3,
          gap: 2,
        },
      }}
    >
      {!onOpenModal && (
        <>
          <FormCreateCandidateChallenge control={control} setValue={setValue} excludeChallenges={excludeChallenges} />
          <Stack
            direction="row"
            sx={{
              justifyContent: "end",
              gap: 1,
            }}
          >
            <IconButtonVariant
              type="button"
              variant="elevated"
              size="large"
              onClick={() => handleToggleOpen(!open)}
              disabled={loading}
            >
              <CloseIcon color="primary" />
            </IconButtonVariant>
            <IconButtonVariant type="button" variant="elevated" size="large" onClick={submit} disabled={loading}>
              {loading ? <CircularProgress color="primary" size={20} /> : <CheckIcon color="success" />}
            </IconButtonVariant>
          </Stack>
        </>
      )}
    </PaperButtonCollapse>
  );
};

export type * from "./logics";
